import { cloneDeep } from 'lodash';

import api from './api';
import { QUESTION_TYPES } from '../constants';

const isQuestion = (blockType) => !!QUESTION_TYPES[blockType];

// returns an object with the number of code and question blocks
const getBlocksCount = (blocks) =>
  Object.values(blocks).reduce(
    (countByType, { type }) => {
      // have separate counts for code blocks and all other types of questions
      if (isQuestion(type))
        countByType[type === 'Code' ? 'code' : 'question']++;
      return countByType;
    },
    { code: 0, question: 0 }
  );

// returns a string like "Question 1 of 3" or "Code Cell 1 of 1"
const generateQuestionString = (
  blockIndex,
  blocksLength,
  blockType,
  cellType
) => {
  const cellTypeLabel =
    {
      readonly: ' - Read Only',
      hidden: ' - Hidden Code',
    }[cellType] || '';
  return blockIndex === null
    ? null
    : `${blockType === 'Code' ? 'Code Cell' : 'Question'} ${
        blockIndex + 1
      } of ${blocksLength}${cellTypeLabel}`;
};

// sometimes the instruction block component is stored below the question, instead of above,
// perhaps due to some race conflict. To ensure that the instructions are always above,
// we manually reorder here.
const sortedWithInstructionsFirst = (blockComponents) => {
  const cmps = blockComponents.slice();
  cmps.sort((a, b) => (a.type === 'Text' ? -1 : 1));
  return cmps;
};

// takes in the JSON definition of a worksheet
export const processLayoutBlocks = (rawBlocks) => {
  const blocks = cloneDeep(rawBlocks);
  const count = getBlocksCount(blocks);

  const getCountForType = (
    (count) => (type) =>
      type === 'Code' ? count.code : count.question
  )(count);

  const getIndexForType = ((count) => {
    let codeCount = 0;
    let questionCount = 0;
    return (type) => {
      if (!isQuestion(type)) return null;
      return type === 'Code' ? codeCount++ : questionCount++;
    };
  })(count);

  blocks.forEach((block, i) => {
    block.isInstructionsBlock = block.type === 'Instructions';
    block.isLastBlock = i === blocks.length - 1;

    block.count = getCountForType(block.type);
    block.index = getIndexForType(block.type);
    block.blockComponents = sortedWithInstructionsFirst(block.block_components);
    delete block.block_components;

    if (block.blockComponents[0]) {
      // code blocks have the cell type field at at the block component level. There will always
      // be only one block component in code blocks. We therefore dig into the first component,
      // and check whether it has the field set. This is used by BlockMeta to display
      // informational text about the field
      block.cellType = block.blockComponents[0].celltype;

      // readonly was an old field used to set the readOnly state of a code field
      // we are now using cellType, but we still support the readonly field for older
      // worksheets
      block.readonly = block.blockComponents[0].readonly || false;
    }

    block.questionString = generateQuestionString(
      block.index,
      block.count,
      block.type,
      block.readOnly ? 'readonly' : block.cellType
    );
  });
  // Old workbooks have a `position` attribute, which is no longer created (and thus the sort has
  // no effect for new workbooks). We only do this sort to ensure backwards compatability for
  // previously generated workbooks, though the authoring process (as of September 2022)
  // actively removes the `position` attribute any time a workbook is edited.
  // New workbooks always have the blocks array maintained in the desired order.
  // This sort could be removed at a future point (suggested after September 2023).
  blocks.sort((a, b) => a.position - b.position);
  return blocks;
};

export const loadTemplate = async (templateUuid) =>
  api(`/api/v1/templates/${templateUuid}/`).then((res) => res.data);

export const getOrCreatePreviewWorksheet = async (
  uuid,
  template,
  rollupKey = null
) => {
  const res = await api(`/api/v1/templates/${template.uuid}/docs/`, {
    method: 'post',
    data: {
      uuid,
      is_rollup: Boolean(rollupKey),
      session_key: rollupKey,
      // All docs created through this method are temporary - not owned resources
      preview: true,
    },
  });
  return res.data;
};

export const getTemplateFromDocument = async (uuid) => {
  const doc = await api(`/api/v1/doc/${uuid}/`)
    .then((res) => res.data)
    .catch(() => null);
  if (doc && doc.templateId) {
    const template = await loadTemplate(doc.templateId);
    if (template) {
      return template;
    }
  } else {
    // TODO Error
  }
};
